import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Collapse, Tag, Button, Upload, message, Spin, Segmented, Badge, Tooltip, Empty, Tabs, Select } from "antd";
import OrderCardProductAssignment from "./OrderCardProductAssignment";
import {
    ReloadOutlined,
    ExportOutlined,
    CheckCircleTwoTone,
    WarningOutlined,
    InboxOutlined,
    FilePdfOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { getConfig } from "../../config";

const { API_BASE_URL } = getConfig();
const AUTH = "VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU";

type Status = "OPEN" | "DONE" | "NO_PLAN";

interface Product {
    jobCode: string;
    description: string | null;
    helios: string | null;
    status: Status;
    pdfFileName: string | null;
}
interface Project {
    projectNo: string;
    projectName: string | null;
    projectManager: string | null;
    openCount: number;
    products: Product[];
}
interface Overview {
    counts: { open: number; done: number; noPlan: number; total: number };
    projects: Project[];
}

type FilterKey = "ALL" | "OPEN" | "NO_PLAN";

const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const SystemPlanPdfTab: React.FC = () => {
    const [data, setData] = useState<Overview | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [filter, setFilter] = useState<FilterKey>("OPEN");
    const [pmFilter, setPmFilter] = useState<string | undefined>(undefined);
    const [uploadingKey, setUploadingKey] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get<Overview>(`${API_BASE_URL}/getSystemPlanPdfOverview?auth=${AUTH}`);
            setData(res.data);
        } catch (e) {
            console.error("Fehler beim Laden der Übersicht:", e);
            message.error("Übersicht konnte nicht geladen werden.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const refresh = async () => {
        setRefreshing(true);
        const hide = message.loading("Daten werden über alle Projekte neu aufgebaut …", 0);
        try {
            await axios.get(`${API_BASE_URL}/refreshSystemPlanPdfOverview?auth=${AUTH}`, { timeout: 600000 });
            await load();
            message.success("Übersicht aktualisiert.");
        } catch (e) {
            console.error("Fehler beim Aktualisieren:", e);
            message.error("Aktualisieren fehlgeschlagen.");
        } finally {
            hide();
            setRefreshing(false);
        }
    };

    const handleUpload = async (project: Project, product: Product, file: File) => {
        const key = `${project.projectNo}::${product.jobCode}`;
        setUploadingKey(key);
        try {
            const pdfBase64 = await fileToBase64(file);
            const res = await axios.post(`${API_BASE_URL}/uploadSystemPlanPdf?auth=${AUTH}`, {
                projectNo: project.projectNo,
                jobCode: product.jobCode,
                fileName: file.name,
                pdfBase64,
            });
            if (res.data?.success) {
                message.success(`Systemplan für ${product.jobCode} hochgeladen.`);
                await load();
            } else {
                message.error(res.data?.message || "Upload fehlgeschlagen.");
            }
        } catch (e) {
            console.error("Upload-Fehler:", e);
            message.error("Upload fehlgeschlagen.");
        } finally {
            setUploadingKey(null);
        }
    };

    const projectManagers = useMemo(() => {
        const set = new Set<string>();
        (data?.projects ?? []).forEach((p) => p.projectManager && set.add(p.projectManager));
        return Array.from(set).sort((a, b) => a.localeCompare(b, "de"));
    }, [data]);

    const visibleProjects = useMemo(() => {
        if (!data) return [];
        const keep = (p: Product) =>
            filter === "ALL" ? true : filter === "OPEN" ? p.status === "OPEN" : p.status === "NO_PLAN";
        return data.projects
            .filter((proj) => !pmFilter || proj.projectManager === pmFilter)
            .map((proj) => ({ ...proj, products: proj.products.filter(keep) }))
            .filter((proj) => proj.products.length > 0);
    }, [data, filter, pmFilter]);

    const renderStatus = (project: Project, product: Product) => {
        const key = `${project.projectNo}::${product.jobCode}`;
        if (product.status === "DONE") {
            return (
                <span>
                    <CheckCircleTwoTone twoToneColor="#52c41a" />{" "}
                    <span style={{ color: "#52c41a" }}>PDF vorhanden</span>
                    {product.pdfFileName ? (
                        <span style={{ color: "#8c8c8c", marginLeft: 6, fontSize: 12 }}>({product.pdfFileName})</span>
                    ) : null}
                </span>
            );
        }
        // OPEN und NO_PLAN sind beide hochladbar — der Upload legt das PDF ab, egal ob ein
        // Helios-Plan verlinkt ist. Unterschied: bei NO_PLAN gibt es keinen Helios-Link,
        // deshalb ein kleiner Hinweis über der Dropzone.
        const dropzone = (
            <Upload.Dragger
                accept="application/pdf,.pdf"
                showUploadList={false}
                multiple={false}
                disabled={uploadingKey === key}
                beforeUpload={(file) => {
                    handleUpload(project, product, file as File);
                    return false; // Upload manuell übernehmen
                }}
                style={{ padding: 0 }}
            >
                <div style={{ padding: "6px 12px", fontSize: 13 }}>
                    {uploadingKey === key ? (
                        <Spin size="small" />
                    ) : (
                        <>
                            <InboxOutlined style={{ color: "#153e8c", marginRight: 6 }} />
                            PDF hierher ziehen oder klicken
                        </>
                    )}
                </div>
            </Upload.Dragger>
        );

        if (product.status === "NO_PLAN") {
            return (
                <div>
                    <div style={{ fontSize: 12, color: "#d48806", marginBottom: 4, textAlign: "right" }}>
                        <WarningOutlined /> Kein Plan im Helios
                    </div>
                    {dropzone}
                </div>
            );
        }
        // OPEN → Drag & Drop Upload
        return dropzone;
    };

    const metric = (label: string, value: number, color: string) => (
        <div style={{ background: "#f5f5f5", borderRadius: 8, padding: "10px 16px", minWidth: 96 }}>
            <div style={{ fontSize: 22, fontWeight: 500, lineHeight: 1.2, color }}>{value}</div>
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>{label}</div>
        </div>
    );

    return (
        <div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
                <span style={{ flex: 1, color: "#595959" }}>
                    Produkte mit Systemplanung, für die noch kein PDF vorliegt — Plan aus Helios holen und hochladen.
                </span>
                <Button icon={<ReloadOutlined />} loading={refreshing} onClick={refresh}>
                    Aktualisieren
                </Button>
            </div>

            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                {metric("offen", data?.counts.open ?? 0, "#153e8c")}
                {metric("erledigt", data?.counts.done ?? 0, "#52c41a")}
                {metric("kein Plan", data?.counts.noPlan ?? 0, "#d48806")}
            </div>

            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
                <Segmented
                    value={filter}
                    onChange={(v) => setFilter(v as FilterKey)}
                    options={[
                        { label: "Nur offen", value: "OPEN" },
                        { label: "Kein Plan", value: "NO_PLAN" },
                        { label: "Alle", value: "ALL" },
                    ]}
                />
                <Select
                    allowClear
                    showSearch
                    placeholder="Projektleiter"
                    style={{ minWidth: 220 }}
                    value={pmFilter}
                    onChange={(v) => setPmFilter(v)}
                    options={projectManagers.map((pm) => ({ value: pm, label: pm }))}
                />
            </div>

            {loading ? (
                <div style={{ textAlign: "center", padding: 48 }}>
                    <Spin size="large" />
                </div>
            ) : visibleProjects.length === 0 ? (
                <Empty description="Keine Produkte in dieser Ansicht" />
            ) : (
                <Collapse defaultActiveKey={visibleProjects.slice(0, 1).map((p) => p.projectNo)}>
                    {visibleProjects.map((project) => (
                        <Collapse.Panel
                            key={project.projectNo}
                            header={
                                <span style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                                    <strong>{project.projectNo}</strong>
                                    <span style={{ color: "#8c8c8c" }}>{project.projectName}</span>
                                    {project.projectManager && (
                                        <Tag color="blue">PL: {project.projectManager}</Tag>
                                    )}
                                    {project.openCount > 0 && (
                                        <Badge
                                            count={`${project.openCount} offen`}
                                            style={{ backgroundColor: "#153e8c" }}
                                        />
                                    )}
                                </span>
                            }
                        >
                            {project.products.map((product) => (
                                <div
                                    key={product.jobCode}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 12,
                                        padding: "10px 4px",
                                        borderBottom: "1px solid #f0f0f0",
                                        flexWrap: "wrap",
                                        opacity: product.status === "DONE" ? 0.7 : 1,
                                    }}
                                >
                                    <Tag color="default" style={{ fontFamily: "monospace" }}>
                                        {product.jobCode}
                                    </Tag>
                                    <span style={{ flex: 1, minWidth: 160 }}>{product.description}</span>
                                    {product.helios ? (
                                        <Tooltip title="Systemplan in Helios öffnen">
                                            <a href={product.helios}>
                                                <Button size="small" icon={<ExportOutlined />}>
                                                    Helios
                                                </Button>
                                            </a>
                                        </Tooltip>
                                    ) : (
                                        <span style={{ width: 84 }} />
                                    )}
                                    <div style={{ minWidth: 220, textAlign: "right" }}>{renderStatus(project, product)}</div>
                                </div>
                            ))}
                        </Collapse.Panel>
                    ))}
                </Collapse>
            )}

            <div style={{ marginTop: 16, color: "#bfbfbf", fontSize: 12 }}>
                <FilePdfOutlined /> „PDF vorhanden" = Materialreservation mit PDF oder hier hochgeladener Systemplan.
            </div>
        </div>
    );
};

/**
 * Seiten-Wrapper mit zwei Tabs (Route /systemplan-pdf-check, bewusst nicht im Menü):
 *  1. Systemplan-PDFs: Produkte mit Systemplanung ohne hochgeladenes PDF.
 *  2. Produkt-Zuordnung (KI): Auftragskarten ohne JobCode — KI-Vorschläge bestätigen (einmalige Bereinigung).
 */
const SystemPlanPdfCheck: React.FC = () => (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
        <Tabs
            defaultActiveKey="systemplan"
            items={[
                { key: "systemplan", label: "Systemplan-PDFs", children: <SystemPlanPdfTab /> },
                { key: "produkt-zuordnung", label: "Produkt-Zuordnung (KI)", children: <OrderCardProductAssignment /> },
            ]}
        />
    </div>
);

export default SystemPlanPdfCheck;
